<template>
  <div class="signin-form-con" id="authpagecontainer">
    <div class="text-center site-logo">
      <SiteLogo />
    </div>
    <div class="site-tagline">
      <h5 class="font-acumin text-center text-primary fw-semibold line-h-120">
        Geofence Messaging <br> in minutes
      </h5>
    </div>
    <div class="account-forms-wrapper">
      <!-- v-show, not v-if: ui.start() needs the element to stay mounted. -->
      <div id="firebaseui-auth-container" v-show="linkState === 'idle'"></div>

      <!-- Magic link (passwordless) sign in. Mirrors FirebaseUI's email flow:
           email -> Next -> (new accounts only) name -> Save, minus the password. -->
      <div class="magic-link-con" v-if="linkState === 'verifying'">
        <h1 class="firebaseui-title">Signing you in</h1>
      </div>

      <div class="magic-link-con" v-else-if="linkState === 'confirm'">
        <h1 class="firebaseui-title">Confirm email</h1>
        <div
          class="firebaseui-textfield mdl-textfield mdl-textfield--floating-label"
          :class="{
            'is-focused': focusedField === 'confirmEmail',
            'is-dirty': !!confirmEmail,
            'is-invalid': !!fieldErrors.confirmEmail,
          }"
        >
          <label class="mdl-textfield__label firebaseui-label" for="ml-confirmEmail">Email</label>
          <input
            class="mdl-textfield__input firebaseui-input"
            type="email"
            id="ml-confirmEmail"
            ref="confirmEmailInput"
            v-model="confirmEmail"
            autocomplete="email"
            @focus="focusedField = 'confirmEmail'; clearFieldError('confirmEmail')"
            @blur="focusedField = ''"
            @keyup.enter="completeSignIn"
          />
        </div>
        <div class="firebaseui-error-wrapper">
          <p
            class="firebaseui-error firebaseui-text-input-error"
            :class="{ 'firebaseui-hidden': !fieldErrors.confirmEmail }"
          >
            {{ fieldErrors.confirmEmail }}
          </p>
        </div>
        <p class="magic-link-error" v-if="magicError">{{ magicError }}</p>
        <div class="firebaseui-card-actions">
          <div class="firebaseui-form-actions">
            <button
              type="button"
              class="firebaseui-button mdl-button mdl-button--primary"
              @click="resetMagicLink"
            >
              Cancel
            </button>
            <button
              type="button"
              class="firebaseui-button mdl-button mdl-button--raised mdl-button--colored"
              @click="completeSignIn"
            >
              {{ submitting ? "Signing in..." : "Sign in" }}
            </button>
          </div>
        </div>
      </div>

      <div class="magic-link-con" v-else-if="linkState === 'sent'">
        <div class="magic-link-title-row">
          <h1 class="firebaseui-title">Check your email</h1>
          <span class="magic-link-timer">{{ countdownLabel }}</span>
        </div>
        <p class="magic-link-copy">
          We sent a sign-in link to <strong>{{ magicEmail }}</strong>.
        </p>
        <p class="magic-link-error" v-if="magicError">{{ magicError }}</p>
        <div class="firebaseui-card-actions">
          <div class="firebaseui-form-actions">
            <button
              type="button"
              class="firebaseui-button mdl-button mdl-button--primary"
              @click="resetMagicLink"
            >
              Back
            </button>
            <!-- Held until the countdown runs out, as sig2_web does. -->
            <button
              type="button"
              class="firebaseui-button mdl-button mdl-button--raised mdl-button--colored"
              :disabled="submitting || secondsLeft > 0"
              @click="resendLink"
            >
              {{ submitting ? "Sending..." : "Resend" }}
            </button>
          </div>
        </div>
      </div>

      <div class="magic-link-con" v-else-if="linkState === 'name'">
        <h1 class="firebaseui-title">Create account</h1>
        <div
          class="firebaseui-textfield mdl-textfield mdl-textfield--floating-label"
          :class="{
            'is-focused': focusedField === 'magicEmail',
            'is-dirty': !!magicEmail,
            'is-invalid': !!fieldErrors.magicEmail,
          }"
        >
          <label class="mdl-textfield__label firebaseui-label" for="ml-name-email">Email</label>
          <input
            class="mdl-textfield__input firebaseui-input"
            type="email"
            id="ml-name-email"
            v-model="magicEmail"
            autocomplete="email"
            @focus="focusedField = 'magicEmail'; clearFieldError('magicEmail')"
            @blur="focusedField = ''"
            @keyup.enter="submitName"
          />
        </div>
        <div class="firebaseui-error-wrapper">
          <p
            class="firebaseui-error firebaseui-text-input-error"
            :class="{ 'firebaseui-hidden': !fieldErrors.magicEmail }"
          >
            {{ fieldErrors.magicEmail }}
          </p>
        </div>
        <div
          class="firebaseui-textfield mdl-textfield mdl-textfield--floating-label"
          :class="{
            'is-focused': focusedField === 'name',
            'is-dirty': !!name,
            'is-invalid': !!fieldErrors.name,
          }"
        >
          <label class="mdl-textfield__label firebaseui-label" for="ml-name">First &amp; last name</label>
          <input
            class="mdl-textfield__input firebaseui-input"
            type="text"
            id="ml-name"
            ref="nameInput"
            v-model="name"
            autocomplete="name"
            @focus="focusedField = 'name'; clearFieldError('name')"
            @blur="focusedField = ''"
            @keyup.enter="submitName"
          />
        </div>
        <div class="firebaseui-error-wrapper">
          <p
            class="firebaseui-error firebaseui-text-input-error"
            :class="{ 'firebaseui-hidden': !fieldErrors.name }"
          >
            {{ fieldErrors.name }}
          </p>
        </div>
        <p class="magic-link-error" v-if="magicError">{{ magicError }}</p>
        <div class="firebaseui-card-actions">
          <div class="firebaseui-form-actions">
            <button
              type="button"
              class="firebaseui-button mdl-button mdl-button--primary"
              @click="resetMagicLink"
            >
              Cancel
            </button>
            <button
              type="button"
              class="firebaseui-button mdl-button mdl-button--raised mdl-button--colored"
              @click="submitName"
            >
              {{ submitting ? "Saving..." : "Save" }}
            </button>
          </div>
        </div>
      </div>

      <div class="magic-link-con" v-else-if="linkState === 'email'">
        <h1 class="firebaseui-title">Sign in with email link</h1>
        <div
          class="firebaseui-textfield mdl-textfield mdl-textfield--floating-label"
          :class="{
            'is-focused': focusedField === 'magicEmail',
            'is-dirty': !!magicEmail,
            'is-invalid': !!fieldErrors.magicEmail,
          }"
        >
          <label class="mdl-textfield__label firebaseui-label" for="ml-magicEmail">Email</label>
          <input
            class="mdl-textfield__input firebaseui-input"
            type="email"
            id="ml-magicEmail"
            ref="magicEmailInput"
            v-model="magicEmail"
            autocomplete="email"
            @focus="focusedField = 'magicEmail'; clearFieldError('magicEmail')"
            @blur="focusedField = ''"
            @keyup.enter="submitEmail"
          />
        </div>
        <div class="firebaseui-error-wrapper">
          <p
            class="firebaseui-error firebaseui-text-input-error"
            :class="{ 'firebaseui-hidden': !fieldErrors.magicEmail }"
          >
            {{ fieldErrors.magicEmail }}
          </p>
        </div>
        <p class="magic-link-error" v-if="magicError">{{ magicError }}</p>
        <div class="firebaseui-card-actions">
          <div class="firebaseui-form-actions">
            <button
              type="button"
              class="firebaseui-button mdl-button mdl-button--primary"
              @click="resetMagicLink"
            >
              Cancel
            </button>
            <button
              type="button"
              class="firebaseui-button mdl-button mdl-button--raised mdl-button--colored"
              @click="submitEmail"
            >
              {{ submitting ? "Checking..." : "Next" }}
            </button>
          </div>
        </div>
      </div>

      <div class="magic-link-con magic-link-con--flush" v-else-if="providerListVisible">
        <button
          type="button"
          class="magic-link-idp-button"
          @click="openMagicLink()"
        >
          <span class="magic-link-idp-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
                 stroke="currentColor" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 2 11 13" />
              <path d="M22 2l-7 20-4-9-9-4 20-7z" />
            </svg>
          </span>
          <span class="magic-link-idp-text">Sign in with email link</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import SiteLogo from '@/components/icons/SiteLogo.vue';
import firebase from 'firebase/compat/app';
import 'firebase/auth'; // Import firebase/auth to make sure the auth module is included

import * as firebaseui from 'firebaseui';
import api from '@/services/api';
import userService from '@/services/userService';
import { watch } from 'vue';
import { config } from '@/helpers/firebaseConfig';
var ui;

// Firebase's documented key name, and the one sig2_web uses.
const EMAIL_LINK_KEY = 'emailForSignIn';
const MAGIC_LINK_EXPIRES_KEY = 'sig2-magic-link-expires-v1';
// Bridges the name typed at sign up across the page load the emailed link causes.
const SIGNUP_PROFILE_KEY = 'sig2-signup-profile-v1';
// Resend throttle shown to the user. Firebase's own code TTL is longer; this
// is a UI affordance, matching sig2_web.
const MAGIC_LINK_TTL_SECONDS = 300;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Every FirebaseUI flow ends in a full page load at this URL, and that reload
// is what lets main.js pick the real landing route from the user's role. The
// magic link flow signs in without leaving the page, so it has to make the
// same navigation itself.
const SIGN_IN_SUCCESS_URL = '/messages';

// Which input each card lands on, and the focusedField value that lifts its
// floating label.
const FOCUS_REFS = {
  email: { name: 'magicEmailInput', field: 'magicEmail' },
  name: { name: 'nameInput', field: 'name' },
  confirm: { name: 'confirmEmailInput', field: 'confirmEmail' },
};

export default {
  name: 'auth',
  components: { SiteLogo },
  data() {
    return {
      fullName: "",
      // 'idle' | 'email' | 'name' | 'sent' | 'confirm' | 'verifying'
      linkState: "idle",
      magicEmail: "",
      confirmEmail: "",
      // One field, as FirebaseUI's Create account page does; split on save.
      name: "",
      magicError: "",
      // MDL's floating label is driven by is-focused / is-dirty classes that
      // its own JS normally toggles. FirebaseUI's JS never sees these fields,
      // so the classes are bound here instead.
      focusedField: "",
      // Per-field validation copy, shown on press rather than disabling the CTA.
      fieldErrors: {},
      // FirebaseUI swaps its container from the provider list to the email
      // form in place. The magic link entry only belongs on the provider list.
      providerListVisible: true,
      submitting: false,
      secondsLeft: 0,
      // The sign-in URL is captured before the query params are scrubbed so a
      // retry after a failure still has an oobCode to work with.
      linkUrl: "",
      countdownTimer: null,
      uiStarted: false,
      authUnsubscribe: null,
    }
  },

  computed: {
    countdownLabel() {
      const total = Math.max(0, this.secondsLeft);
      const mins = Math.floor(total / 60);
      const secs = total % 60;
      return `${mins}:${secs < 10 ? "0" + secs : secs}`;
    },
  },

  beforeUnmount() {
    this.stopCountdown();
    if (this.authUnsubscribe) this.authUnsubscribe();
  },

  mounted() {
    // Check if user is logged in and redirect to home page
    if (this.$root.loggedInUser) {
      document.location.href = "/";
    }

    // Firebase is already initialized in main.js before this component mounts

    setTimeout(function () {
      if (document.getElementById('authpagecontainer')) {
        document.getElementById('authpagecontainer').style.opacity = 1;
      }
    }, 2000);

    this.restoreCountdown();

    // If we arrived here from a magic link, finish that sign in instead of
    // rendering the FirebaseUI widget behind the redirect.
    const startedFromLink = this.handleEmailLinkArrival();

    // Fires in this tab once the emailed link signs in, and in the tab that
    // requested the link too, because Firebase syncs auth state across tabs.
    // That second case is the one that leaves a countdown running on a card
    // nobody is waiting for any more.
    this.authUnsubscribe = firebase.auth().onAuthStateChanged((user) => {
      // Magic link cards only. FirebaseUI navigates on its own once it has
      // finished writing the display name, and stepping in early would cut
      // that short.
      if (user && this.linkState !== 'idle') this.enterApp();
    });

    if (startedFromLink) {
      return;
    }

    this.startFirebaseUi();
  },

  methods: {
    startFirebaseUi() {
      if (this.uiStarted) return;
      this.uiStarted = true;

      var uiConfig = {
        signInSuccessUrl: SIGN_IN_SUCCESS_URL,
        signInFlow: 'popup',
        signInOptions: [
          firebase.auth.GoogleAuthProvider.PROVIDER_ID,
          firebase.auth.EmailAuthProvider.PROVIDER_ID,
        ],
        callbacks: {
          signInSuccessWithAuthResult: async (authResult) => {
            return true;
          }
        }
      };

      // Ensure firebase.auth() is available before using it
      if (!firebase.auth()) {
        console.error('Firebase Auth is not available.');
        return;
      }

      ui = firebaseui.auth.AuthUI.getInstance();
      if (!ui) {
        ui = new firebaseui.auth.AuthUI(firebase.auth());
      }

      ui.start('#firebaseui-auth-container', uiConfig);

      const observer = new MutationObserver(async (mutationsList, observer) => {
        this.providerListVisible = !!document.querySelector(
          '#firebaseui-auth-container .firebaseui-id-page-provider-sign-in'
        );

        const emailInput = document.querySelector('.firebaseui-id-email');
        const nameInput = document.querySelector('.firebaseui-id-name');
        if (emailInput) {
          try{
            let data = await userService.getInvitation(emailInput.value);
            if(data!=''){
              data = data?.at(0);
              this.fullName = `${data.FirstName} ${data.LastName}`;
            }
          }catch(e) {
            // this.fullName = "Not registered";
          }

          if (nameInput && this.fullName) {
            nameInput.value = this.fullName;
            nameInput.blur();
            nameInput.parentElement.classList.add('is-dirty');
            nameInput.parentElement.classList.add('disabled');
            nameInput.parentElement.classList.add('disabled-dark');
          }
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });
    },

    // A full page load rather than a router push: main.js resolves the landing
    // route by role during bootstrap, and only bootstrap runs that. replace()
    // rather than assign() so Back does not return to a spent link.
    enterApp() {
      this.stopCountdown();
      // Leaves SIGNUP_PROFILE_KEY alone: main.js consumes it on the next load.
      this.clearStoredEmail();
      window.location.replace(SIGN_IN_SUCCESS_URL);
    },

    // Puts the card back to the plain sign in options. Needed because the
    // confirm state is reached without FirebaseUI having been started, so
    // otherwise there would be no way back to Google or password sign in.
    resetMagicLink() {
      this.stopCountdown();
      this.clearStoredEmail();
      // Only cleared here, on an explicit cancel. A successful sign in leaves
      // it for main.js to consume and clear.
      try {
        localStorage.removeItem(SIGNUP_PROFILE_KEY);
      } catch (e) {
        // ignore
      }
      this.secondsLeft = 0;
      this.magicError = '';
      this.fieldErrors = {};
      this.magicEmail = '';
      this.confirmEmail = '';
      this.name = '';
      this.focusedField = '';
      this.linkState = 'idle';
      // Wait for v-show to reveal the container before FirebaseUI renders into it.
      this.$nextTick(() => this.startFirebaseUi());
    },

    // Returns true when this page load is a magic link landing, in which case
    // the FirebaseUI widget is not started.
    handleEmailLinkArrival() {
      if (!firebase.auth().isSignInWithEmailLink(window.location.href)) {
        return false;
      }

      this.linkUrl = window.location.href;
      const stored = this.readStoredEmail();

      if (!stored) {
        // Link opened on a different device or browser than it was requested
        // from, so the address has to be re-entered to complete sign in.
        this.goTo('confirm');
        return true;
      }

      this.linkState = 'verifying';
      this.signInWithLink(stored);
      return true;
    },

    openMagicLink() {
      this.magicError = '';
      this.fieldErrors = {};
      this.goTo('email');
    },

    // Switching card + landing on its input, which is what FirebaseUI does on
    // every page of the email flow. MDL's floating label is driven by the
    // is-focused class here, so focusedField has to move with the caret.
    goTo(state) {
      this.linkState = state;
      const ref = FOCUS_REFS[state];
      if (!ref) return;
      this.focusedField = ref.field;
      this.$nextTick(() => {
        const el = this.$refs[ref.name];
        if (el && typeof el.focus === 'function') el.focus();
      });
    },

    // FirebaseUI's own wording, so the two email forms read the same.
    emailError(email) {
      if (!email) return 'Enter your email address to continue';
      if (!EMAIL_RE.test(email)) return "That email address isn't correct";
      return '';
    },

    clearFieldError(field) {
      if (!this.fieldErrors[field]) return;
      const next = { ...this.fieldErrors };
      delete next[field];
      this.fieldErrors = next;
    },

    // Step 1. Mirrors FirebaseUI's Next: look the address up, and only ask
    // for a name when there is no account yet.
    async submitEmail() {
      if (this.submitting) return;

      const email = this.magicEmail.trim();
      const emailErr = this.emailError(email);
      if (emailErr) {
        this.fieldErrors = { magicEmail: emailErr };
        return;
      }

      this.fieldErrors = {};
      this.magicError = '';
      this.submitting = true;
      try {
        const existing = await userService.isUserAllowed(email);
        if (existing?.user?.length) {
          // Known account: nothing left to ask for.
          await this.sendLink();
          return;
        }
        this.goTo('name');
      } catch (e) {
        this.magicError =
          e?.message || 'Unable to check that email. Please try again.';
      } finally {
        this.submitting = false;
      }
    },

    // Step 2. Name is required because Firebase supplies none for magic
    // link users, so without it the account would be created blank.
    async submitName() {
      if (this.submitting) return;

      const email = this.magicEmail.trim();
      const errors = {};
      const emailErr = this.emailError(email);
      if (emailErr) errors.magicEmail = emailErr;
      if (!this.name.trim()) errors.name = 'Enter your first & last name';
      if (Object.keys(errors).length) {
        this.fieldErrors = errors;
        return;
      }

      this.fieldErrors = {};
      const parts = this.name.trim().split(/\s+/);
      const profile = {
        firstName: parts.shift() || '',
        lastName: parts.join(' '),
      };
      // Kept as a fallback so main.js can still name the account if
      // /savesaasuser below fails.
      this.storeSignupProfile(profile);

      this.submitting = true;
      this.magicError = '';
      try {
        // Same order as sig2_web's sign up: create the account, then send the
        // link. Doing it now rather than after sign in is what makes the name
        // survive the link being opened on another device.
        await userService.createSaasUser({ ...profile, email });
        await this.sendLink();
      } catch (e) {
        this.magicError =
          e?.message || 'Unable to create your account. Please try again.';
      } finally {
        this.submitting = false;
      }
    },

    async resendLink() {
      if (this.submitting) return;
      if (this.secondsLeft > 0) {
        this.magicError = `You can request another link in ${this.countdownLabel}.`;
        return;
      }
      this.submitting = true;
      try {
        await this.sendLink();
      } finally {
        this.submitting = false;
      }
    },

    // Callers validate and own the submitting flag.
    async sendLink() {
      const email = this.magicEmail.trim();
      this.magicError = '';
      try {
        await userService.sendMagicLink(email);
        this.storeEmail(email);
        this.startCountdown();
        this.linkState = 'sent';
      } catch (e) {
        // The axios interceptor rejects with the response body, not the error.
        this.magicError =
          e?.message || 'Unable to send the sign-in link. Please try again.';
      }
    },

    async completeSignIn() {
      if (this.submitting) return;

      const email = this.confirmEmail.trim();
      const emailErr = this.emailError(email);
      if (emailErr) {
        this.fieldErrors = { confirmEmail: emailErr };
        return;
      }

      this.submitting = true;
      this.magicError = '';
      await this.signInWithLink(email);
      this.submitting = false;
    },

    async signInWithLink(email) {
      try {
        this.storeEmail(email);
        await firebase.auth().signInWithEmailLink(email, this.linkUrl);
        // main.js's onAuthStateChanged handler takes over from here: it calls
        // /isuserallowed, stores the token and redirects by role.
        this.clearStoredEmail();
        this.stopCountdown();
        this.scrubUrl();
      } catch (error) {
        this.scrubUrl();
        this.magicError = this.authErrorMessage(error);
        this.confirmEmail = email;
        this.goTo('confirm');
      }
    },

    authErrorMessage(error) {
      switch (error?.code) {
        case 'auth/invalid-email':
          return 'Please enter a valid email address.';
        case 'auth/expired-action-code':
        case 'auth/invalid-action-code':
          return 'This link expired or was already used. Send a new one.';
        case 'auth/unauthorized-continue-uri':
          return 'This app domain is not allowlisted in Firebase Authentication.';
        case 'auth/too-many-requests':
          return 'Too many attempts. Please try again later.';
        case 'auth/network-request-failed':
          return 'Network error. Please check your connection and try again.';
        default:
          return error?.message || 'Unable to sign in. Please try again.';
      }
    },

    // The query params are stripped so a refresh doesn't retry a spent code,
    // but this.linkUrl keeps the original so a retry still works.
    scrubUrl() {
      window.history.replaceState(
        {},
        document.title,
        `${window.location.origin}${window.location.pathname}`
      );
    },

    readStoredEmail() {
      try {
        return localStorage.getItem(EMAIL_LINK_KEY) || '';
      } catch (e) {
        return '';
      }
    },

    storeSignupProfile(profile) {
      try {
        localStorage.setItem(SIGNUP_PROFILE_KEY, JSON.stringify(profile));
      } catch (e) {
        // ignore
      }
    },

    storeEmail(email) {
      try {
        localStorage.setItem(EMAIL_LINK_KEY, email);
      } catch (e) {
        // Private browsing modes can throw on write; sign in still works as
        // long as the link is opened in this same browser.
      }
    },

    clearStoredEmail() {
      try {
        localStorage.removeItem(EMAIL_LINK_KEY);
        localStorage.removeItem(MAGIC_LINK_EXPIRES_KEY);
      } catch (e) {
        // ignore
      }
    },

    startCountdown() {
      const expiresAt = Date.now() + MAGIC_LINK_TTL_SECONDS * 1000;
      try {
        localStorage.setItem(MAGIC_LINK_EXPIRES_KEY, String(expiresAt));
      } catch (e) {
        // ignore
      }
      this.tickFrom(expiresAt);
    },

    // Opening the emailed link reloads the page, so the countdown is restored
    // from localStorage rather than restarted.
    restoreCountdown() {
      let expiresAt = 0;
      try {
        expiresAt = Number(localStorage.getItem(MAGIC_LINK_EXPIRES_KEY)) || 0;
      } catch (e) {
        expiresAt = 0;
      }
      if (expiresAt > Date.now()) {
        this.magicEmail = this.readStoredEmail();
        this.linkState = 'sent';
        this.tickFrom(expiresAt);
      }
    },

    tickFrom(expiresAt) {
      this.stopCountdown();
      const tick = () => {
        this.secondsLeft = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
        if (this.secondsLeft <= 0) this.stopCountdown();
      };
      tick();
      this.countdownTimer = window.setInterval(tick, 1000);
    },

    stopCountdown() {
      if (this.countdownTimer) {
        window.clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
  },
};
</script>

<style lang="scss">
.signin-form-con {
  border-radius: 20px;
  background: rgba(255, 255, 255, 1);
  padding: 60px 50px 48px;
  width: 400px;
  min-height: 632px;

  .disabled-dark {
    opacity: 1!important;
  }


  @media screen and (max-width: 992px) {
    width: 100%;
    padding: 30px;
    min-height: 600px;
    height: auto;
  }
}

.site-logo {
  margin-bottom: 45px;
}

.site-tagline {
  margin-bottom: 70px;

  h5 {
    font-size: 20px;
    font-style: normal;
    line-height: 24px;
  }
}


.firebaseui-idp-button {
  background: #fff !important;
  display: flex !important;
  align-items: center;
  gap: 20px;

  &:hover {
    background: #dadada !important;
  }
}

.firebaseui-idp-text {
  color: rgba(0, 0, 0, 0.54) !important;
  padding-left: 0 !important;
}

.mdl-button--raised {
  box-shadow: 0px 1px 1px 0px rgba(0, 0, 0, 0.17), 0px 0px 1px 0px rgba(0, 0, 0, 0.08) !important
}

.footercopyright .md-layout-item,
.footermenu .md-layout-item {
  font-weight: normal !important
}

.footermenu {
  margin-left: 92px
}

.firebaseui-card-content {
  padding: 0 !important
}

.firebaseui-idp-button,
.firebaseui-tenant-button {
  max-width: 100% !important
}

.firebaseui-list-item:first-child {
  margin-bottom: 45px !important
}

.firebaseui-list-item:nth-of-type(2) {
  .firebaseui-idp-icon-wrapper {
    display: inline-block;
    width: 20px;
    height: 20px;
    background: url("../assets//images/icons/Email-icon.svg");
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;

    img {
      display: none !important;
    }
  }
}

.mdl-shadow--2dp {
  box-shadow: none !important
}

.firebaseui-card-header {
  padding: 0 !important
}

.firebaseui-container {
  background: none !important
}

.mdl-button--raised.mdl-button--colored {
  border-radius: 3px;
  background: #4066CF !important;
  color: #fff !important;
  padding: 8.5px 16.4px;
  font-family: "Roboto";
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;

  &:hover {
    background: #6B87D4 !important;
    color: #fff !important;
  }
}

.mdl-shadow--2dp {
  box-shadow: none !important
}

.mdl-textfield {
  padding: 20px 0 0 !important;
}

.mdl-textfield__label:after {
  top: 98%;
}

.firebaseui-error-wrapper {
  position: relative;
  top: 19px;
}

// Flush with the top of the wrapper, where FirebaseUI's own cards start, so
// the card does not jump down 28px when you take the email link path instead
// of the email/password one. The provider list entry re-adds a gap below.
.magic-link-con {
  margin-top: 0;
  text-align: left;
}

// Matches the 45px that .firebaseui-list-item:first-child puts between the
// Google and email buttons. The 15px under the email button does not add to
// this: it collapses into the idp list's own 1em bottom margin, which then
// collapses with this one, so whichever is larger wins.
.magic-link-con--flush {
  margin-top: 45px;
}

// Mirrors .firebaseui-idp-button so this reads as one of the buttons above:
// same 40px min-height, 8px/16px padding, 2px radius, raised shadow and
// Roboto 14px/500 label.
.magic-link-idp-button {
  display: flex;
  align-items: center;
  gap: 20px;
  width: 100%;
  min-height: 40px;
  padding: 8px 16px;
  border: none;
  border-radius: 2px;
  background: #fff;
  box-shadow: 0px 1px 1px 0px rgba(0, 0, 0, 0.17),
    0px 0px 1px 0px rgba(0, 0, 0, 0.08);
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: #dadada;
  }
}

.magic-link-idp-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
  color: #4066CF;
}

.magic-link-idp-text {
  font-family: "Roboto", Helvetica, Arial, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  color: rgba(0, 0, 0, 0.54);
  text-transform: none;
}

.magic-link-title-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;

  // .firebaseui-title carries the 16px bottom padding for the row.
  .firebaseui-title {
    padding-bottom: 0;
  }

  padding-bottom: 16px;
}

// FirebaseUI keeps this row nowrap, which is fine for its own short labels but
// pushes ours past the 400px card. Wrapping is the lesser evil.
.magic-link-con .firebaseui-form-actions {
  white-space: normal;
}

// mdl-button--colored forces its background with !important, so the disabled
// state has to be spelled out or a held resend button still looks pressable.
.firebaseui-form-actions .mdl-button[disabled] {
  opacity: 0.5;
  cursor: default;
  pointer-events: none;
}

.magic-link-timer {
  font-size: 15px;
  font-weight: 600;
  color: #344D7A;
  // Stops the countdown from jittering as the digits change.
  font-variant-numeric: tabular-nums;
}

.magic-link-copy {
  font-family: "Roboto";
  font-size: 13px;
  line-height: 18px;
  color: #6b6b6b;
  margin: 0 0 12px;
  text-align: left;
}

.magic-link-error {
  font-size: 12px;
  line-height: 16px;
  color: #dd2c00;
  margin: 10px 0 0;
  text-align: left;
}

// The buttons carry FirebaseUI's own classes, so they inherit its styling and
// this file's .mdl-button--raised.mdl-button--colored override. Both sit in a
// single .firebaseui-form-actions cell, as FirebaseUI's own Sign in / Create
// account pages do, so .firebaseui-button's 8px margin-left is the gap between
// them and .firebaseui-card-actions supplies the padding. Nothing to add here.
</style>